// ------------------ IMPORTS -------------------

import { useTranslation } from "react-i18next";
import { Select } from "@lockerpm/design";
import { DateTime } from "luxon";

// Types and Enums
import {
  PassportPeriodType,
  PassportActionType,
} from "#src/services/endpoint/passports/enums";

// Hooks
import { useTimeFrameLogic } from "../hooks/useTimeFrameLogic";

// Components
import { TimeInDayPicker, secondsToFormattedTime } from "./TimeInDayPicker";
import { DateInMonthPicker } from "./DateInMonthPicker";

// ------------------ TYPES -------------------

interface TimeFrameFieldsProps {
  action: PassportActionType;
  timeFrameEnabled: boolean;
  setTimeFrameEnabled: (enabled: boolean) => void;
  timeFrameType: PassportPeriodType;
  setTimeFrameType: (type: PassportPeriodType) => void;
  timeFrameRepeatEvery: number;
  setTimeFrameRepeatEvery: (repeatEvery: number) => void;
  timeFrameStartTime: number;
  setTimeFrameStartTime: (startTime: number) => void;
  timeFrameEndTime: number;
  setTimeFrameEndTime: (endTime: number) => void;
  timeFrameRepeatOn: number[];
  setTimeFrameRepeatOn: (repeatOn: number[]) => void;
  timeFrameRepeatOnDate: number;
  setTimeFrameRepeatOnDate: (repeatOn: number) => void;
}

// ------------------ COMPONENT -------------------

export const TimeFrameFields = ({
  action,
  timeFrameEnabled,
  setTimeFrameEnabled,
  timeFrameType,
  setTimeFrameType,
  timeFrameRepeatEvery,
  setTimeFrameRepeatEvery,
  timeFrameStartTime,
  setTimeFrameStartTime,
  timeFrameEndTime,
  setTimeFrameEndTime,
  timeFrameRepeatOn,
  setTimeFrameRepeatOn,
  timeFrameRepeatOnDate,
  setTimeFrameRepeatOnDate,
}: TimeFrameFieldsProps) => {
  // ------------------ HOOKS -------------------

  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });
  const {
    weekDayOptions,
    periodOptions,
    repeatEveryOptions,
    handleWeekDayToggle,
    handleMonthDayChange,
    verdictText,
  } = useTimeFrameLogic({
    timeFrameRepeatEvery,
    timeFrameType,
    timeFrameRepeatOn,
    setTimeFrameRepeatOn,
    timeFrameRepeatOnDate,
    setTimeFrameRepeatOnDate,
  });

  // ------------------ RENDER -------------------

  return (
    <div className="mt-6">
      {/* Checkbox */}
      <div className="flex items-center gap-2 mb-6">
        <input
          type="checkbox"
          id="timeFrameEnabled"
          checked={timeFrameEnabled}
          onChange={(e) => setTimeFrameEnabled(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label
          htmlFor="timeFrameEnabled"
          className="text-gray-v2-700 text-sm font-medium"
        >
          {t(
            `modal.addAndEdit.time_frame.checkbox.${action === PassportActionType.ACCEPT ? "allow" : "block"}`
          )}
        </label>
      </div>
      {/* Checkbox end */}

      {/* Body */}
      {timeFrameEnabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Type */}
            <div>
              <p className="text-gray-v2-700 mb-1.5 text-sm">
                {t("modal.addAndEdit.time_frame.period.label")}
              </p>
              <Select
                className="w-full"
                value={timeFrameType}
                onChange={(value) => setTimeFrameType(value)}
                options={periodOptions}
              />
            </div>
            {/* Type end */}

            <div></div>

            {/* Repeat every */}
            {timeFrameType !== PassportPeriodType.DAILY && (
              <div>
                <p className="text-gray-v2-700 mb-1.5 text-sm">
                  {t("modal.addAndEdit.time_frame.repeat_every")}
                </p>
                <Select
                  className="w-full"
                  value={timeFrameRepeatEvery}
                  onChange={(value) => setTimeFrameRepeatEvery(value)}
                  options={repeatEveryOptions}
                />
              </div>
            )}
            {/* Repeat every end */}

            {/* Weekly Repeat On */}
            {timeFrameType === PassportPeriodType.WEEKLY && (
              <div>
                <p className="text-gray-v2-700 mb-1.5 text-sm">
                  {t("modal.addAndEdit.time_frame.repeat_on")}
                </p>
                <div className="flex rounded-lg border border-gray-v2-300 overflow-hidden w-full divide-x divide-gray-v2-300">
                  {weekDayOptions.map((day) => (
                    <div
                      key={day.value}
                      onClick={() => handleWeekDayToggle(day.value)}
                      className={[
                        "flex-1 py-2.5 text-sm text-center font-semibold transition-colors cursor-pointer",
                        timeFrameRepeatOn.includes(day.value)
                          ? "bg-primary text-white"
                          : "bg-white text-gray-v2-700 hover:bg-gray-v2-100",
                      ].join(" ")}
                    >
                      {day.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Weekly Repeat On end */}

            {/* Monthly Day of Month */}
            {timeFrameType === PassportPeriodType.MONTHLY && (
              <div>
                <p className="text-gray-v2-700 mb-1.5 text-sm">
                  {t("modal.addAndEdit.time_frame.repeat_on")}
                </p>
                <DateInMonthPicker
                  value={timeFrameRepeatOnDate}
                  setValue={handleMonthDayChange}
                  placeholder={t("modal.addAndEdit.time_frame.select_date")}
                />
              </div>
            )}
            {/* Monthly Day of Month end */}
          </div>

          {/* Start and End Time */}
          <div className="mt-4">
            <p className="text-gray-v2-700 mb-1.5 text-sm">
              {t("modal.addAndEdit.time_frame.time_frame")}
            </p>

            <div className="flex items-center gap-2 flex-col sm:flex-row">
              <TimeInDayPicker
                value={timeFrameStartTime}
                setValue={(val) => setTimeFrameStartTime(val)}
              />
              <span className="hidden sm:inline-block">-</span>
              <TimeInDayPicker
                value={timeFrameEndTime}
                setValue={(val) => setTimeFrameEndTime(val)}
              />
            </div>
          </div>
          {/* Start and End Time end */}

          {/* Verdict */}
          <div className="w-full mt-6">
            <p className="text-md text-gray-v2-600">
              <span className="font-semibold text-gray-v2-900">
                {t("modal.addAndEdit.time_frame.verdict.label")}:&nbsp;
              </span>

              <span>
                {action === PassportActionType.ACCEPT
                  ? t("modal.addAndEdit.time_frame.verdict.desc_allow_start")
                  : t("modal.addAndEdit.time_frame.verdict.desc_block_start")}
              </span>

              {!!verdictText && <span>{verdictText}</span>}

              {timeFrameStartTime >= 0 &&
                timeFrameEndTime > timeFrameStartTime && (
                  <>
                    <span>
                      {t("modal.addAndEdit.time_frame.verdict.desc_end")}
                    </span>
                    <span className="font-semibold text-gray-v2-900">
                      {secondsToFormattedTime(timeFrameStartTime)}
                      &nbsp;-&nbsp;
                      {secondsToFormattedTime(timeFrameEndTime)}
                      &nbsp;({DateTime.now().toFormat("ZZZZ")})
                    </span>
                  </>
                )}
            </p>
          </div>
          {/* Verdict end */}
        </>
      )}
      {/* Body end */}
    </div>
  );
};
